/*
  Outside-service features (wave 3) — names and texts the UI needs even before the server answered.
  The server is the source of truth: GET /api/integrations/status (signed in) and
  GET /api/integrations/sign-in-options (public, for the sign-in page). See backend/feature_flags.py and
  docs/INTEGRATIONS_SETUP.md. Use them through `useIntegrationStatus()` / `useFeature()`
  (`@/hooks/useIntegrationStatus`) and `<NotConfigured feature=… />` (`@/components/ui/not-configured`).
*/
import api from '@/lib/api';

export const FEATURE_STATUS = {
  ON: 'on',
  OFF: 'off',
  NOT_CONFIGURED: 'not_configured',
};

export const INTEGRATIONS_ADMIN_PATH = '/admin?tab=integrations';

/** Labels / descriptions used until (or when) the status endpoint cannot be reached. Keys = backend keys. */
export const FEATURES = {
  microsoft_sso: {
    label: 'Microsoft 365 sign-in',
    description: 'Sign in with the company Microsoft 365 account instead of a Sara password.',
  },
  passkeys: {
    label: 'Passkeys (Face ID / fingerprint)',
    description: 'Sign in on a phone with Face ID or a fingerprint after the first password sign-in.',
  },
  email_outbound: {
    label: 'Outgoing email',
    description: 'Send email from Sara: weekly digests, reminders and chase emails.',
  },
  email_inbound: {
    label: 'Incoming email (mailbox)',
    description: 'Read a shared mailbox, e.g. consultant replies and supplier emails forwarded to Sara.',
  },
  onedrive: {
    label: 'OneDrive / SharePoint',
    description: 'Link each project to its OneDrive folder and check the required folders.',
  },
  quickbooks: {
    label: 'QuickBooks Online',
    description: "Send invoices to the company's QuickBooks accounts.",
  },
  // Owner decision 9 Oct 2026: the books are in QuickBooks Desktop — read only, shown next to the platform's numbers.
  quickbooks_desktop: {
    label: 'QuickBooks Desktop',
    description: 'Reads invoices, payments, items, customers, vendors and costs from the company\'s QuickBooks Desktop '
      + 'through the Web Connector and shows them next to the platform\'s own numbers. Never writes to QuickBooks.',
  },
  ai: {
    label: 'AI assistant (Gemini)',
    description: 'Answers questions about live data, morning briefs and suggestions.',
  },
  // The wave-3 AI features: same key as `ai`, but OFF until an admin switches them on (Admin › Integrations).
  ai_extras: {
    label: 'AI extras',
    description: 'AI wording of the morning brief, reading a pasted consultant reply, supplier chase drafts, pinned '
      + 'Dashboard questions, delivery-note photo reading and project blockers wording.',
  },
};

export const FEATURE_KEYS = Object.keys(FEATURES);

/**
 * The feature's status object, or a "not configured" placeholder built from FEATURES (`placeholder: true`:
 * the server has not answered — still loading, the request failed, or nobody is signed in).
 */
export function featureFromStatus(statusResponse, key) {
  const fromServer = statusResponse?.features?.[key];
  if (fromServer) return fromServer;
  const meta = FEATURES[key] || { label: key, description: '' };
  return {
    key,
    label: meta.label,
    description: meta.description,
    status: FEATURE_STATUS.NOT_CONFIGURED,
    configured: false,
    enabled: false,
    admin_url: INTEGRATIONS_ADMIN_PATH,
    placeholder: true,
  };
}

export const featureLabel = (key) => FEATURES[key]?.label || key;

/** The feature key of the wave-3 AI features (morning brief wording, "From email", chase wording, pinned questions,
 * DN photo, blockers wording). The AI page itself is `ai`. */
export const AI_EXTRAS = 'ai_extras';

/**
 * The `text` of a <NotConfigured feature="ai_extras"> note: while AI extras is switched off it first says who can
 * switch it on, then what to do meanwhile; while it is not set up only what to do meanwhile (NotConfigured's own
 * texts cover the rest). `undefined` = NotConfigured's default text.
 */
export function aiExtrasHelp(feature, meanwhile = '') {
  const off = feature?.status === FEATURE_STATUS.OFF;
  const text = [off ? 'An admin can switch it on in Admin › Integrations › AI extras.' : '', meanwhile].filter(Boolean).join(' ');
  return text || undefined;
}

/**
 * Sign-in page options (no token needed): { microsoft_sso: { enabled }, passkeys: { enabled } }.
 * Never throws: a failed request means "no extra sign-in buttons".
 */
export async function loadSignInOptions() {
  try {
    const res = await api.get('/integrations/sign-in-options');
    return {
      microsoft_sso: { enabled: Boolean(res.data?.microsoft_sso?.enabled) },
      passkeys: { enabled: Boolean(res.data?.passkeys?.enabled) },
    };
  } catch {
    return { microsoft_sso: { enabled: false }, passkeys: { enabled: false } };
  }
}

/** True when a 503 from `feature_flags.require_enabled` (detail.code === 'feature_not_enabled'). */
export function isFeatureNotEnabledError(error) {
  return error?.response?.status === 503 && error?.response?.data?.detail?.code === 'feature_not_enabled';
}
